import PropTypes from "prop-types";
import { fetchImage } from "../../nonUIFuncs";

const PERSON_IMAGE_MAP = {
  firstperson_s: "FMTY_1_S.png",
  firstperson_p_i: "FMTY_1_P_I.png",
  firstperson_p_e: "FMTY_1_P_E.png",
  secondperson_s: "FMTY_2_S.png",
  secondperson_p: "FMTY_2_P.png",
  thirdperson_s: "FMTY_3_S.png",
  thirdperson_p: "FMTY_3_P.png",
  fourthperson_s: "FMTY_4.png",
};

/**
 * Start downloading every person-marker image (there are only 8). Games where
 * pictures are only up for a moment (Whack-A-Mole, Bubble Pop) call this when
 * they open: otherwise a marker could still be loading when its picture has
 * already gone.
 */
export const preloadPersonMarkers = () => {
  Object.values(PERSON_IMAGE_MAP).forEach((file) => {
    const img = new Image();
    img.src = fetchImage(file);
  });
};

const PERSON_LOOKUP = [
  { key: "FirstPerson", t: "1st" },
  { key: "SecondPerson", t: "2nd" },
  { key: "ThirdPerson", t: "3rd" },
  { key: "FourthPerson", t: "4th" },
];

const resolvePersonKey = (personBase, data) => {
  const lookup = PERSON_LOOKUP.find((p) => p.key === personBase);
  if (!lookup) return null;
  const isPlural = data[`Plural_${lookup.t}_Person`] === true;
  if (isPlural) {
    let key = `${personBase}_p`;
    if (personBase === "FirstPerson") {
      key = data.TypeLink?.toUpperCase().includes("WE/EX")
        ? `${personBase}_p_e`
        : `${personBase}_p_i`;
    }
    return key.toLowerCase();
  }
  return `${personBase}_s`.toLowerCase();
};

// Mirrors the logic in FromMeToYou/index.js getRoundData to ensure consistency.
export const parsePersonMarkers = (data) => {
  if (!data?.Arrow) return null;
  const split = data.Arrow.split("_");
  if (split.length < 3) return null;
  const subject = resolvePersonKey(split[0], data);
  const object = resolvePersonKey(split[2], data);
  if (!subject || !object) return null;
  return { subject, object };
};

const PersonMarkerIcon = ({ data }) => {
  const parsed = parsePersonMarkers(data);
  if (!parsed) return null;

  const subjectFile = PERSON_IMAGE_MAP[parsed.subject];
  const objectFile = PERSON_IMAGE_MAP[parsed.object];
  if (!subjectFile || !objectFile) return null;

  return (
    <div style={{ position: "relative", width: "100%", paddingBottom: "100%" }}>
      <img
        src={fetchImage(subjectFile)}
        alt=""
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "72%",
          height: "72%",
          borderRadius: "50%",
        }}
      />
      <img
        src={fetchImage(objectFile)}
        alt=""
        style={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: "62%",
          height: "62%",
          borderRadius: "50%",
        }}
      />
    </div>
  );
};

const VocabImage = ({ appArt, dataset, english, data, showPersonMarkers, className, imgClassName }) => {
  const hasPersonMarkers = showPersonMarkers && data?.Arrow && data.Arrow.length > 0;

  if (!appArt || appArt.length === 0) {
    return (
      <div className={className} style={{ position: "relative", display: "inline-block", width: "100%" }}>
        <div className="Dict-tile-no-image">
          <span>{english ? english.charAt(0) : ""}</span>
        </div>
        {hasPersonMarkers && (
          <div style={{ position: "absolute", bottom: "4%", right: "4%", width: "38%", aspectRatio: "1" }}>
            <PersonMarkerIcon data={data} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={className} style={{ position: "relative", display: "inline-block", width: "100%" }}>
      <img
        alt={english || ""}
        src={fetchImage(appArt, dataset)}
        className={imgClassName}
      />
      {hasPersonMarkers && (
        <div style={{ position: "absolute", bottom: "4%", right: "4%", width: "38%", aspectRatio: "1" }}>
          <PersonMarkerIcon data={data} />
        </div>
      )}
    </div>
  );
};

export default VocabImage;
export { PersonMarkerIcon };

VocabImage.propTypes = {
  appArt: PropTypes.string,
  dataset: PropTypes.string,
  english: PropTypes.string,
  data: PropTypes.object,
  showPersonMarkers: PropTypes.bool,
  className: PropTypes.string,
  imgClassName: PropTypes.string,
};

PersonMarkerIcon.propTypes = {
  data: PropTypes.object,
};
